/**
 * Placement inference + points engine (isolated / swappable).
 */

import { isFixtureComplete } from "../competition/standingsTypes";
import { DEFAULT_POINTS_TABLE, seasonKeyFromTimestamp } from "./rankingTypes";

/**
 * Infer placement tier from card / result text.
 * @returns {'winner'|'runner_up'|'semi'|'quarter'|'r16'|'participant'|null}
 */
export function inferPlacementTierFromText(text) {
  const t = String(text || "").toLowerCase();
  if (!t) return null;
  if (/champion|winner|🏆\s*final.*won|won.*final|title/.test(t) && /final/.test(t)) {
    // weak — prefer explicit isWin + final
  }
  if (/\bqf\b|quarter[- ]?final/.test(t)) return "quarter";
  if (/\bsf\b|semi[- ]?final/.test(t)) return "semi";
  if (/round\s*of\s*16|r16|1\/8/.test(t)) return "r16";
  if (/final|championship/.test(t) && !/semi|quarter/.test(t)) {
    return "final"; // winner vs runner decided by isWin
  }
  return null;
}

/**
 * @param {object} card
 * @returns {'winner'|'runner_up'|'semi'|'quarter'|'r16'|'participant'|null}
 */
export function inferCardPlacement(card) {
  const text = `${card.roundName || ""} ${card.resultHeadline || ""} ${card.result || ""} ${card.scoreSummary || ""}`;
  const tier = inferPlacementTierFromText(text);
  if (tier === "final") return card.isWin ? "winner" : "runner_up";
  if (tier === "semi") return "semi";
  if (tier === "quarter") return "quarter";
  if (tier === "r16") return "r16";
  return null;
}

/**
 * Extract player placements from a completed Single Elimination draw.
 * Pure — does not mutate competition.
 *
 * @param {object} competition
 * @param {object} draw
 * @returns {Array<{ playerId: string, placement: string, competitionId: string, drawId: string }>}
 */
export function extractPlacementsFromDraw(competition, draw) {
  const fixtures = draw?.fixtures || [];
  const entries = competition?.managedEntries || [];
  const byEntry = new Map(entries.map((e) => [e.entryId, e]));
  const competitionId = competition?.competitionId || competition?.id || null;
  const drawId = draw?.drawId || null;

  const playerIdsForEntry = (entryId) => {
    const e = byEntry.get(entryId);
    if (!e) return [];
    if (Array.isArray(e.memberPlayerIds) && e.memberPlayerIds.length) {
      return e.memberPlayerIds.filter(Boolean);
    }
    if (Array.isArray(e.members)) {
      return e.members.map((m) => m.playerId).filter(Boolean);
    }
    return [];
  };

  const out = [];
  const push = (entryId, placement) => {
    playerIdsForEntry(entryId).forEach((playerId) => {
      out.push({ playerId, placement, competitionId, drawId });
    });
  };

  const playable = fixtures.filter(
    (f) => f && f.status !== "bye" && (f.entryAId || f.entryBId)
  );
  if (!playable.length) return out;

  const maxRound = Math.max(...playable.map((f) => f.roundIndex ?? 0));
  const byRoundName = (re) =>
    playable.filter((f) => re.test(String(f.roundName || "").toLowerCase()));

  const finals =
    byRoundName(/final/).filter((f) => !/semi|quarter/.test(String(f.roundName || "").toLowerCase())) ||
    [];
  const finalMatch =
    finals.find((f) => (f.roundIndex ?? 0) === maxRound) ||
    playable.find((f) => (f.roundIndex ?? 0) === maxRound);

  if (finalMatch?.winnerEntryId && isFixtureComplete(finalMatch)) {
    push(finalMatch.winnerEntryId, "winner");
    const loser =
      finalMatch.winnerEntryId === finalMatch.entryAId
        ? finalMatch.entryBId
        : finalMatch.entryAId;
    if (loser) push(loser, "runner_up");
  }

  const semis = byRoundName(/semi/);
  semis.forEach((f) => {
    if (!isFixtureComplete(f) || !f.winnerEntryId) return;
    const loser = f.winnerEntryId === f.entryAId ? f.entryBId : f.entryAId;
    if (loser) push(loser, "semi");
  });

  const quarters = byRoundName(/quarter/);
  quarters.forEach((f) => {
    if (!isFixtureComplete(f) || !f.winnerEntryId) return;
    const loser = f.winnerEntryId === f.entryAId ? f.entryBId : f.entryAId;
    if (loser) push(loser, "quarter");
  });

  // Participation for confirmed entries
  entries
    .filter((e) => e.status === "confirmed" || e.status === "pending" || !e.status)
    .forEach((e) => {
      playerIdsForEntry(e.entryId).forEach((playerId) => {
        out.push({
          playerId,
          placement: "participant",
          competitionId,
          drawId,
        });
      });
    });

  return out;
}

/**
 * Extract placements from all managed draws on a competition.
 * @param {object} competition
 */
export function extractPlacementsFromCompetition(competition) {
  const draws = competition?.managedDraws || [];
  /** @type {ReturnType<typeof extractPlacementsFromDraw>} */
  const all = [];
  draws.forEach((d) => {
    all.push(...extractPlacementsFromDraw(competition, d));
  });
  return all;
}

/**
 * Points for a single placement tier.
 * @param {string} placement
 * @param {typeof DEFAULT_POINTS_TABLE} [table]
 */
export function pointsForPlacement(placement, table = DEFAULT_POINTS_TABLE) {
  switch (placement) {
    case "winner":
      return table.tournamentWinner;
    case "runner_up":
      return table.runnerUp;
    case "semi":
      return table.semiFinal;
    case "quarter":
      return table.quarterFinal;
    case "r16":
      return table.roundOf16;
    case "participant":
      return table.participation;
    default:
      return 0;
  }
}

/**
 * Collapse duplicate placements — keep best tier per competition for a player.
 * @param {Array<{ playerId: string, placement: string, competitionId?: string }>} rows
 */
export function bestPlacementsPerCompetition(rows) {
  const rank = {
    winner: 5,
    runner_up: 4,
    semi: 3,
    quarter: 2,
    r16: 1,
    participant: 0,
  };
  /** @type {Map<string, { playerId: string, placement: string, competitionId?: string }>} */
  const best = new Map();
  (rows || []).forEach((r) => {
    const key = `${r.playerId}::${r.competitionId || "_"}`;
    const prev = best.get(key);
    if (!prev || (rank[r.placement] || 0) > (rank[prev.placement] || 0)) {
      best.set(key, r);
    }
  });
  return [...best.values()];
}

/**
 * Sum points for placements + match results.
 *
 * @param {object} input
 * @param {Array<{ placement: string, competitionId?: string }>} [input.placements]
 * @param {Array<{ isWin?: boolean, completedAt?: number }>} [input.cards]
 * @param {typeof DEFAULT_POINTS_TABLE} [input.pointsTable]
 * @param {number | null} [input.seasonYear] null = all-time
 */
export function calculatePointsFromSources(input = {}) {
  const table = input.pointsTable || DEFAULT_POINTS_TABLE;
  const seasonYear = input.seasonYear ?? null;
  let placements = bestPlacementsPerCompetition(input.placements || []);
  if (seasonYear != null) {
    // Placements lack timestamps — filter cards below; placements filtered by caller usually
  }
  let points = 0;
  const breakdown = {
    tournament: 0,
    matches: 0,
    byPlacement: /** @type {Record<string, number>} */ ({}),
  };

  placements.forEach((p) => {
    const pts = pointsForPlacement(p.placement, table);
    points += pts;
    breakdown.tournament += pts;
    breakdown.byPlacement[p.placement] = (breakdown.byPlacement[p.placement] || 0) + pts;
  });

  (input.cards || []).forEach((c) => {
    if (seasonYear != null) {
      const y = Number(seasonKeyFromTimestamp(c.completedAt));
      if (y !== seasonYear) return;
    }
    const pts = c.isWin ? table.matchWin : table.matchLoss;
    points += pts;
    breakdown.matches += pts;
  });

  // Soft placement signals from match cards when draw placements are absent
  if (!(input.placements || []).length) {
    const softRows = [];
    (input.cards || []).forEach((c) => {
      if (seasonYear != null) {
        const y = Number(seasonKeyFromTimestamp(c.completedAt));
        if (y !== seasonYear) return;
      }
      const inferred = inferCardPlacement(c);
      if (inferred) {
        softRows.push({
          playerId: "_",
          placement: inferred,
          competitionId: c.competitionId || `card_${c.matchId}`,
        });
      }
    });
    bestPlacementsPerCompetition(softRows).forEach((p) => {
      const pts = pointsForPlacement(p.placement, table);
      points += pts;
      breakdown.tournament += pts;
      breakdown.byPlacement[p.placement] = (breakdown.byPlacement[p.placement] || 0) + pts;
    });
  }

  return { points, breakdown, algorithmId: table.algorithmId };
}
